import objectToFormData from "../helpers/objectToFormData";
import axios, {AxiosError, AxiosInstance, AxiosResponse} from "axios";
import {Company, Login} from "../types/Subscription";

class SubscriptionService {
    private instance: AxiosInstance;

    constructor() {
        const token = localStorage.getItem('zrToken')

        this.instance = axios.create({
            baseURL: 'https://license.zr-digital.de/api/',
            headers: token ? {'Authorization': token}: {}
        })
    }

    setToken(token: string|null) {
        if (token) {
            this.instance.defaults.headers['Authorization'] = token
            localStorage.setItem('zrToken', token)
        } else {
            delete this.instance.defaults.headers['Authorization']
            localStorage.removeItem('zrToken')
        }
    }

    hasToken() {
        return !!this.instance.defaults.headers['Authorization']
    }

    addInterceptors(
        onFulfill?: (response: AxiosResponse) => AxiosResponse,
        onError?: (error: AxiosError | Error) => Promise<AxiosError>
    ) {
        this.instance.interceptors.response.use(onFulfill, onError)
    }

    async login(data: Partial<Login>) {
        return this.callApi('auth/login', 'post', data )
    }

    async logout() {
        return this.callApi('auth/logout')
    }

    async register(data: Partial<Company & Login>) {
        return this.callApi('auth/register', 'post', data )
    }

    async passwordReset(data: {email: string}) {
        return this.callApi('auth/password-reset', 'post', data)
    }

    async passwordResetConfirm(data: Partial<{
        token: string,
        password: string,
        password_confirmation: string
    }>) {
        return this.callApi('auth/change-password', 'post', data)
    }

    async getUser() {
        return this.callApi('user')
    }

    async updateUser(data: Partial<Company>) {
        return this.callApi('user', 'post', data)
    }

    async getPlans(plugin: string) {
        return this.callApi('plans/' + plugin)
    }

    async createIntent() {
        return this.callApi('payment-intent')
    }

    async subscribe(data: object) {
        return this.callApi('subscribe', 'post', data)
    }

    async getPaymentMethods() {
        return this.callApi('payments')
    }

    async deletePaymentMethod(id: string) {
        return this.callApi('payments/' + id, 'delete')
    }

    async getSubscriptions(plugin: string) {
        return this.callApi('subscriptions/' + plugin)
    }

    async addPlanToSub(plugin: string, price_id: string) {
        return this.callApi('subscriptions/' + plugin + '/add/' + price_id)
    }

    async removePlanFromSub(plugin: string, price_id: string) {
        return this.callApi('subscriptions/' + plugin + '/remove/' + price_id)
    }

    async stopSub(plugin: string) {
        return this.callApi('subscriptions/' + plugin + '/stop')
    }

    async resumeSub(plugin: string) {
        return this.callApi('subscriptions/' + plugin + '/resume')
    }

    async changeHost(plugin: string, data: {host: string, key: string|undefined}) {
        return this.callApi('subscriptions/' + plugin + '/change-host', 'post', data)
    }

    async callApi(route: string, method = 'get',  data: object = {}, files: Blob[] = []) {
        if (!route.includes('auth') && !this.hasToken()) {
            throw new Error('No token')
        } else {
            return await this.instance.request({
                method: method,
                url: route,
                data: objectToFormData({...data, ...files}),
                responseType: "json"
            })
        }
    }
}

export default SubscriptionService;
